import * as React from 'react'
import {
  DescriptionText,
  MainTitleText,
  PropInline,
  PropsInlineWrapper,
  PropStack,
  TitleText,
} from './styles'

import { getValuePropsForCountry } from './constants'
import { ValueProp } from './types'
import Carousel from '../../organisms/carousel'
import { CarouselProvider } from '../../organisms/carousel/context'
import { Breakpoints, Colors, Media, Spacing } from '@doordash/design-language'
import styled from 'styled-components'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import MediaQuery from 'react-responsive'
import * as Exp from '../../../experimentConstants'
import { CountryCode } from '@doordash/lib-i18n'

import * as t from './strings'

type Props = {
  valueProps?: ValueProp[]
  isInline?: boolean
  mainTitle?: string
  maxNumToRender?: number
  country?: CountryCode
}

const DataWrapper = styled.div`
  border: 1px solid ${Colors.SystemGrey5};
  padding: ${Spacing.Medium}px;
  border-radius: ${Spacing.XxxSmall}px;
  margin: ${Spacing.None}px ${Spacing.XxLarge}px;
  max-height: fit-content;
  text-align: center;
  ${Media.WideScreen`
  max-width: calc(100vw - ${Spacing.XxxLarge}px);
`}
`

type CardProps = {
  data: ValueProp
}

type ComponentProps = {
  valueProps: ValueProp[]
}

type VerticalComponentProps = {
  isInline: boolean
  valueProps: ValueProp[]
}

const CardComponent: React.FunctionComponent<CardProps> = ({ data }) => {
  return (
    <React.Fragment>
      <div>{data.image}</div>
      <TitleText tag="h2">{data.title}</TitleText>
      <DescriptionText tag="p">{data.description}</DescriptionText>
    </React.Fragment>
  )
}

const PropList: React.FunctionComponent<ComponentProps> = ({ valueProps }) => {
  const component = valueProps.map(data => {
    return (
      <div key={`${data.title}${data.description}`}>
        <CardComponent data={data} />
      </div>
    )
  })
  return <React.Fragment>{component}</React.Fragment>
}

/* This is the Carousel component that will be shown on a mobile device */
const SwipeComponent: React.FunctionComponent<ComponentProps> = ({
  valueProps,
}) => {
  return (
    <CarouselProvider>
      <Carousel isFullScreen={false}>
        {valueProps.map(data => {
          return (
            <div key={`${data.title}${data.description}`} id={data.title}>
              <DataWrapper>
                <CardComponent data={data} />
              </DataWrapper>
            </div>
          )
        })}
      </Carousel>
    </CarouselProvider>
  )
}

/* This component shows content inline or stacked format on devices other than mobile */
const VerticalComponent: React.FunctionComponent<VerticalComponentProps> = ({
  isInline,
  valueProps,
}) => {
  return (
    <React.Fragment>
      {isInline ? (
        <PropsInlineWrapper>
          <PropInline>
            <PropList valueProps={valueProps} />
          </PropInline>
        </PropsInlineWrapper>
      ) : (
        <PropStack>
          <PropList valueProps={valueProps} />
        </PropStack>
      )}
    </React.Fragment>
  )
}

/**
 *
 * @param valueProps The list of value propositions to pass in
 * If left blank, will use props defined in constants
 * @param isInline If true, use InlineChildren to stack horizontally
 * Will default to false and use StackChildren if left empty
 */
export const ValueProps: React.FunctionComponent<Props> = ({
  isInline = false,
  mainTitle = t.default.mainTitle(),
  country,
}) => {
  const isGraduationQ3 = isFeatureFlagEnabled(Exp.DX_CRO_LP_GRADUATION_Q3_2020)
  const valuePropsList = getValuePropsForCountry(country)
  return (
    <React.Fragment>
      <MainTitleText>{mainTitle}</MainTitleText>
      {isGraduationQ3 && (
        <MediaQuery maxWidth={Breakpoints.Mobile}>
          <SwipeComponent valueProps={valuePropsList} />
        </MediaQuery>
      )}
      {!isGraduationQ3 && (
        <MediaQuery maxWidth={Breakpoints.Mobile}>
          <VerticalComponent isInline={isInline} valueProps={valuePropsList} />
        </MediaQuery>
      )}
      <MediaQuery minWidth={Breakpoints.Mobile + 1}>
        <VerticalComponent isInline={isInline} valueProps={valuePropsList} />
      </MediaQuery>
    </React.Fragment>
  )
}

export default ValueProps
